import { useState, useEffect, useCallback } from 'react'
import { MessageSquare, Download, Star, AlertTriangle, Clock, ChevronRight } from 'lucide-react'
import { cn } from '../utils/cn'
import { format } from 'date-fns'
import { useTranslation } from '@/i18n'
import { API_BASE_URL } from '@/config'
import { auth } from '@/firebase'
import { useAuth } from '@/hooks/useAuth'

interface ConversationSession {
  id: string
  status: 'active' | 'ended'
  startedAt: string
  endedAt?: string
  messageCount: number
  satisfaction?: number
  escalated?: boolean
  preview?: string
  mode?: string
}

interface ConversationMessage {
  id: string
  role: 'user' | 'assistant' | 'system' | 'agent'
  content: string
  timestamp: string
  imageData?: string
  metadata?: {
    processingTime?: number
    confidence?: number
    sources?: string[]
  }
}

export default function Conversations() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [sessions, setSessions] = useState<ConversationSession[]>([])
  const [selectedSession, setSelectedSession] = useState<string | null>(null)
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [filter, setFilter] = useState<'all' | 'active' | 'ended' | 'escalated'>('all')
  const [projectId, setProjectId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const getToken = useCallback(async () => {
    return await auth.currentUser?.getIdToken()
  }, [])

  // Fetch the first available project
  const fetchProjectId = useCallback(async () => {
    try {
      const token = await getToken()
      if (!token) return null
      const res = await fetch(`${API_BASE_URL}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return null
      const data = await res.json()
      if (data.projects && data.projects.length > 0) {
        return data.projects[0].id as string
      }
      return null
    } catch {
      return null
    }
  }, [getToken])

  // Fetch sessions for the project
  const fetchSessions = useCallback(async (projId: string) => {
    try {
      setLoading(true)
      setError(null)
      const token = await getToken()
      if (!token) {
        setError('Not authenticated')
        setLoading(false)
        return
      }
      const res = await fetch(`${API_BASE_URL}/projects/${projId}/sessions?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        setError('Failed to load conversations')
        setLoading(false)
        return
      }
      const data = await res.json()
      const sessionList: ConversationSession[] = (data.sessions || []).map((s: any) => ({
        id: s.id,
        status: s.status || 'active',
        startedAt: s.startedAt || new Date().toISOString(),
        endedAt: s.endedAt || undefined,
        messageCount: s.messageCount || 0,
        satisfaction: s.satisfaction,
        escalated: s.mode === 'queued' || s.mode === 'human' || !!s.escalationReason,
        preview: s.lastMessage || s.escalationReason || '',
        mode: s.mode,
      }))
      setSessions(sessionList)
    } catch {
      setError('Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }, [getToken])

  // Fetch messages for a selected session (uses agent route for admin view)
  const fetchMessages = useCallback(async (sessionId: string) => {
    try {
      setMessagesLoading(true)
      const token = await getToken()
      if (!token) return
      const res = await fetch(`${API_BASE_URL}/projects/${projectId}/sessions/${sessionId}/messages?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        setMessages([])
        return
      }
      const data = await res.json()
      const msgs: ConversationMessage[] = (data.messages || []).map((m: any) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp || new Date().toISOString(),
        imageData: m.imageData,
        metadata: m.metadata,
      }))
      setMessages(msgs)
    } catch {
      setMessages([])
    } finally {
      setMessagesLoading(false)
    }
  }, [getToken])

  // Initialize: fetch project, then sessions
  useEffect(() => {
    let cancelled = false
    async function init() {
      const projId = await fetchProjectId()
      if (cancelled) return
      if (projId) {
        setProjectId(projId)
        await fetchSessions(projId)
      } else {
        setLoading(false)
        setError('No project found. Please create a project first.')
      }
    }
    init()
    return () => { cancelled = true }
  }, [fetchProjectId, fetchSessions])

  // Fetch messages when a session is selected
  useEffect(() => {
    if (selectedSession) {
      fetchMessages(selectedSession)
    } else {
      setMessages([])
    }
  }, [selectedSession, fetchMessages])

  const filterLabels: Record<string, string> = {
    all: t('conversations.all'),
    active: t('conversations.active'),
    ended: t('conversations.ended'),
    escalated: t('conversations.escalated'),
  }

  const filteredSessions = sessions.filter(session => {
    if (filter === 'all') return true
    if (filter === 'active') return session.status === 'active'
    if (filter === 'ended') return session.status === 'ended'
    if (filter === 'escalated') return session.escalated
    return true
  })

  const handleExport = () => {
    const data = { session: selectedSession, messages, exportedAt: new Date().toISOString() }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `conversation-${selectedSession}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const renderSatisfaction = (score?: number) => {
    if (score === undefined) return null
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={cn('h-3 w-3', i < score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300')} />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('conversations.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('conversations.subtitle')}</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex h-[calc(100vh-220px)] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="w-96 border-r border-gray-200 flex flex-col">
          <div className="p-3 border-b border-gray-200">
            <div className="flex gap-1">
              {(['all', 'active', 'ended', 'escalated'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-full capitalize',
                    filter === f ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'
                  )}
                >
                  {filterLabels[f]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">{t('common.loading')}</div>
            ) : filteredSessions.length === 0 ? (
              <div className="p-4 text-center text-gray-500">{t('conversations.noConversationsFound')}</div>
            ) : (
              filteredSessions.map(session => (
                <button
                  key={session.id}
                  onClick={() => setSelectedSession(session.id)}
                  className={cn(
                    'w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors',
                    selectedSession === session.id && 'bg-indigo-50 border-l-2 border-l-indigo-500'
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        session.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      )}>
                        {session.status === 'active' ? t('conversations.active') : t('conversations.ended')}
                      </span>
                      {session.escalated && <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />}
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-900 truncate">{session.preview}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      {format(new Date(session.startedAt), 'MMM d, HH:mm')}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{session.messageCount} {t('conversations.msgs')}</span>
                      {renderSatisfaction(session.satisfaction)}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          {selectedSession ? (
            <>
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-gray-400" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{t('conversations.session')} {selectedSession}</h3>
                    <p className="text-xs text-gray-500">{messages.length} {t('conversations.messages')}</p>
                  </div>
                </div>
                <button
                  onClick={handleExport}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  {t('common.export')}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messagesLoading ? (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <p className="text-sm">{t('common.loading')}</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <p className="text-sm">{t('conversations.noMessages') || 'No messages in this session'}</p>
                  </div>
                ) : messages.map(msg => (
                  <div key={msg.id} className={cn('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    {msg.role !== 'user' && (
                      <div className={cn('w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0', msg.role === 'agent' ? 'bg-green-100 text-green-600' : 'bg-indigo-100 text-indigo-600')}>{msg.role === 'agent' ? 'AG' : 'AI'}</div>
                    )}
                    <div className={cn('max-w-[70%]')}>
                      {msg.imageData && (
                        <img src={`data:image/jpeg;base64,${msg.imageData}`} alt="User uploaded" className="max-w-[200px] rounded-lg mb-2 shadow-sm" />
                      )}
                      <div className={cn('rounded-lg px-4 py-2.5 text-sm', msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-800')}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">{format(new Date(msg.timestamp), 'HH:mm:ss')}</span>
                        {msg.metadata?.processingTime && <span className="text-xs text-gray-400">{msg.metadata.processingTime}ms</span>}
                        {msg.metadata?.sources && msg.metadata.sources.length > 0 && (
                          <span className="text-xs text-indigo-400">{t('conversations.sources')}: {msg.metadata.sources.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0">U</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">{t('conversations.selectConversation')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
